import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {HttpClient, HttpParams} from "@angular/common/http";
import {AuthService} from "@auth0/auth0-angular";
import {
  BookingState,
  BookingStore,
  createInitialBookingCriteria
} from "./booking.store";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  tap,
  timeout
} from "rxjs";
import {
  Booking,
  BookingCriteria,
  BookingListItem,
  BookingTopUnit
} from "../../data-access/api-booking/Booking";
import {arrayRemove, arrayUpsert} from "@datorama/akita";
import {retryBackoff} from "../../data-access/retry.service";
import {PageResponse} from "../../data-access/PageResponse";


@Injectable({ providedIn: 'root' })
export class BookingStoreService {
  private readonly baseUrl: string;
  private apiV1: string = environment.apiV1;
  private baseRef: string = environment.origin;
  private serviceCore: string = environment.service.core;

  /**
   * Source unique des critères de la liste. `ReplaySubject` et non `BehaviorSubject` : rien n'est
   * chargé avant que des critères réels soient poussés, ce qui évite une requête parasite au
   * montage (cf. DECISIONS.md du 2026-10-01).
   */
  private readonly criteria$: ReplaySubject<BookingCriteria>
    = new ReplaySubject<BookingCriteria>(1);

  /** Le debounce ne porte que sur la frappe : un changement de page doit rester immédiat. */
  private readonly searchTerm$: Subject<string>
    = new Subject<string>();

  /** Les écouteurs vivent le temps de la session : ne jamais les installer deux fois. */
  private listening: boolean = false;

  constructor(
    private store: BookingStore,
    private httpClient: HttpClient,
    private authService: AuthService
  ) {
    this.baseUrl = environment.local;
  }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public init(): void {
    this.authService.isLoading$
      .pipe(
        // wait until SDK finished init/callback
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        // safe: token is ready, interceptor will attach it
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/services'} });
            return EMPTY;
          }
          if (!this.listening) {
            this.listening = true;
            this.listenToCriteria();
            this.listenToSearchTerm();
          }
          // Akita démarre un store avec `loading: true` : sans cette remise à zéro, les écrans
          // branchés sur `isLoading$` resteraient en chargement toute la session.
          this.setLoading(false);
          return EMPTY;
        }),
        catchError((err) => {
          console.error('[bootstrap bookings] init failed', err);
          this.setLoading(false);
          return EMPTY;
        })
      ).subscribe();
  }

  /**
   * Un seul abonnement pour la session : `switchMap` annule la requête encore en vol dès que les
   * critères changent, ce qui supprime les réponses arrivant dans le désordre pendant que
   * l'utilisateur tape ou enchaîne les pages.
   */
  private listenToCriteria(): void {
    this.criteria$
      .pipe(
        distinctUntilChanged((left: BookingCriteria, right: BookingCriteria) =>
          this.criteriaKey(left) === this.criteriaKey(right)),
        tap(() => this.setLoading(true)),
        switchMap((criteria: BookingCriteria) => this.fetchPage(criteria)
          .pipe(
            timeout({ first: 15000 }),
            retryBackoff(1, 400),
            tap((res: PageResponse<BookingListItem>) => {
              this.store.update((state: BookingState) => ({
                ...state,
                bookings: res.items ? [ ...res.items ] : [],
                bookingCriteria: { ...criteria },
                bookingTotalItems: res.totalItems,
                bookingTotalPage: res.totalPages,
                error: undefined
              }));
            }),
            catchError((error) => {
              this.store.setError(error);
              return of(null);
            })
          )),
        tap(() => this.setLoading(false))
      )
      .subscribe();
  }

  private listenToSearchTerm(): void {
    this.searchTerm$
      .pipe(
        debounceTime(350),
        distinctUntilChanged()
      )
      .subscribe((term: string) => this.patchCriteria({ term, page: 0 }));
  }

  /**
   * Point d'entrée des onglets : fixe les critères propres à l'onglet (son statut) et déclenche
   * le chargement de la première page.
   */
  public loadFirstPage(criteria: Partial<BookingCriteria>): void {
    this.criteria$.next({
      ...createInitialBookingCriteria(),
      ...criteria,
      page: 0
    });
  }

  /** Recherche serveur ; le terme est debouncé avant d'atteindre le réseau. */
  public search(term: string): void {
    this.searchTerm$.next(term ? term.trim() : '');
  }

  public goToPage(pageIndex: number): void {
    // nz-table indexe les pages à partir de 1, l'API à partir de 0.
    this.patchCriteria({ page: Math.max(0, pageIndex - 1) });
  }

  public changePageSize(size: number): void {
    this.patchCriteria({ size, page: 0 });
  }

  public changeSort(sort: string): void {
    this.patchCriteria({ sort, page: 0 });
  }

  /** Recharge la page courante, après une création ou une modification par exemple. */
  public reloadCurrentPage(): void {
    this.criteria$.next({ ...this.store.getValue().bookingCriteria });
  }

  private patchCriteria(patch: Partial<BookingCriteria>): void {
    const current: BookingCriteria = this.store.getValue().bookingCriteria;
    this.criteria$.next({ ...current, ...patch });
  }

  private fetchPage(criteria: BookingCriteria): Observable<PageResponse<BookingListItem>> {
    const url: string = `${this.baseRef}${this.serviceCore}${this.apiV1}bookings/search`;

    let httpParams: HttpParams = new HttpParams()
      .set('page', String(criteria.page))
      .set('size', String(criteria.size))
      .set('sort', criteria.sort);

    if (criteria.term) {
      httpParams = httpParams.set('q', criteria.term);
    }
    if (criteria.status) {
      httpParams = httpParams.set('status', criteria.status);
    }
    if (criteria.residenceId) {
      httpParams = httpParams.set('residenceId', criteria.residenceId);
    }

    return this.httpClient.get<PageResponse<BookingListItem>>(url, { params: httpParams });
  }

  private criteriaKey(criteria: BookingCriteria): string {
    return [
      criteria.term,
      criteria.status,
      criteria.residenceId,
      criteria.page,
      criteria.size,
      criteria.sort
    ].join('|');
  }

  /**
   * Classement des logements les plus réservés. L'agrégation est faite par le serveur sur
   * l'ensemble des séjours : la calculer depuis le store donnerait un classement faux, puisque
   * celui-ci ne contient qu'une page.
   */
  public loadTopUnits(residenceId: string | null, limit: number = 10): void {
    let httpParams: HttpParams = new HttpParams().set('limit', String(limit));
    if (residenceId) {
      httpParams = httpParams.set('residenceId', residenceId);
    }

    this.httpClient.get<BookingTopUnit[]>(
      `${this.baseRef}${this.serviceCore}${this.apiV1}bookings/top-units`,
      { params: httpParams }
    )
      .pipe(
        timeout({ first: 10000 }),
        retryBackoff(1, 400),
        tap((units: BookingTopUnit[]) => {
          this.store.update((state: BookingState) => ({
            ...state,
            topUnits: units ? [ ...units ] : []
          }));
        }),
        catchError((error) => {
          // Widget non critique : pas d'erreur bloquante pour ça.
          console.error('[bookings top units] load failed', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   *
   * UPDATE & DELETE
   *
   */

  /**
   * Répercute une écriture sur la page courante. Le store ne détenant plus que les colonnes de
   * la liste, le `Booking` complet renvoyé par l'API est projeté ici. Une création n'est pas
   * insérée de force : elle n'appartient peut-être pas à la page ni à l'onglet courants —
   * utiliser `reloadCurrentPage()` dans ce cas.
   */
  public upsertBooking(booking: Booking): void {
    if (!booking || !booking.id) {
      return;
    }

    this.store.update((state: BookingState) => {
      const isInCurrentPage: boolean = state.bookings
        .some((item: BookingListItem) => item.id === booking.id);

      return {
        ...state,
        bookings: isInCurrentPage
          ? arrayUpsert(state.bookings, booking.id, this.toListItem(booking))
          : state.bookings
      };
    });
  }

  public async deleteBooking(bookingId: string): Promise<boolean> {
    return new Promise<boolean>((resolve => {
      this.store.update((state: BookingState) => ({
        ...state,
        bookings: arrayRemove(state.bookings, bookingId)
      }));
      resolve(true);
    }));
  }

  private toListItem(booking: Booking): BookingListItem {
    return {
      id: booking.id,
      dateAdd: booking.dateAdd,
      dateUpd: booking.dateUpd,
      enabled: booking.enabled,
      externalId: booking.externalId,
      ceriId: booking.ceriId,
      bookingNumber: booking.bookingNumber,
      bookingStartDate: booking.bookingStartDate,
      bookingEndDate: booking.bookingEndDate,
      bookingNights: booking.bookingNights,
      bookingStatus: booking.bookingStatus,
      bookingUnitNumber: booking.bookingUnitNumber ?? booking.bookingUnit?.unitNumber ?? null,
      bookingUnitTypeName: booking.bookingUnitTypeName ?? booking.bookingUnitType?.unitTypeName ?? null,
      bookingCustomerFullName: booking.bookingCustomerFullName
        ?? booking.bookingCustomer?.customerFullName ?? null,
      bookingCustomerId: booking.bookingCustomer?.id ?? null,
      bookingResidenceId: booking.bookingResidence?.id,
    };
  }

}
